import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Box, Chip, CircularProgress, Divider, List, ListItemButton,
    ListItemIcon, ListItemText, Popover, Tab, Tabs, Typography,
} from '@mui/material';
import {
    AccountBalance as AccountIcon,
    Assignment as TaskIcon,
    Business as ClientIcon,
    Folder as ProjectIcon,
    Group as GroupIcon,
    Person as UserIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/system';
import styled from '@emotion/styled';
import StatusBadge from './StatusBadge';
import ActiveChip from './ActiveChip';

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledTabs = styled(Tabs)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: theme.spacing(5),
    flexShrink: 0,
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    minHeight: theme.spacing(5),
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: theme.typography.size.sm,
}));

const TabLabelBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const TabCountChip = styled(Chip)(({theme}) => ({
    height: theme.spacing(2),
    fontSize: theme.typography.size.xxs,
    marginLeft: theme.spacing(0.5),
}));

const ResultsArea = styled(Box)(({ theme }) => ({
    overflow: 'auto',
    flexGrow: 1,
}));

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const EmptyBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
}));

const ResultListItem = styled(ListItemButton, {
    shouldForwardProp: prop => prop !== '$isDeleted' && prop !== '$isHighlighted',
})(({ theme, $isDeleted, $isHighlighted }) => ({
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    backgroundColor: $isDeleted
        ? theme.palette.action.disabled
        : $isHighlighted
        ? alpha(theme.palette.primary.main, 0.08)
        : 'transparent',
    textDecoration: $isDeleted ? 'line-through' : 'none',
    cursor: 'pointer',
}));

const ResultItemIcon = styled(ListItemIcon)({
    minWidth: 36,
});

const PrimaryBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const ItemName = styled(Typography, {
    shouldForwardProp: prop => prop !== '$isDeleted',
})(({ theme, $isDeleted }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: $isDeleted ? theme.palette.text.disabled : theme.palette.text.primary,
}));

const SecondaryCaption = styled(Typography, {
    shouldForwardProp: prop => prop !== '$isDeleted',
})(({ theme, $isDeleted }) => ({
    color: $isDeleted ? theme.palette.text.disabled : theme.palette.text.secondary,
}));

const FooterBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    backgroundColor: theme.palette.action.hover,
}));

const ResultCount = styled(Typography)({
    marginLeft: 'auto',
});

const ViewAllLink = styled(Typography)({
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ALL_TYPES = ['clients', 'accounts', 'projects', 'tasks', 'groups', 'users'];

const STATUS_DOMAIN = { accounts: 'account', projects: 'project', tasks: 'task' };

const TYPE_CONFIG = {
    clients:  { label: 'Clients',  Icon: ClientIcon  },
    accounts: { label: 'Accounts', Icon: AccountIcon },
    projects: { label: 'Projects', Icon: ProjectIcon },
    tasks:    { label: 'Tasks',    Icon: TaskIcon    },
    groups:   { label: 'Groups',   Icon: GroupIcon   },
    users:    { label: 'Users',    Icon: UserIcon    },
};

const getItemLabel = (type, item) => {
    if (type === 'users') return `${item.first_name} ${item.last_name}`;
    return item.name || '';
};

const getItemSecondary = (type, item) => {
    switch (type) {
        case 'clients':  return item.slug || '';
        case 'accounts': return item.client || '';
        case 'projects': return '';
        case 'tasks':    return '';
        case 'groups':   return item.account || '';
        case 'users':    return [item.clientName, item.role].filter(Boolean).join(' • ');
        default: return '';
    }
};

const getItemUrl = (type, item) => {
    switch (type) {
        case 'clients':  return `/client/${item.slug}`;
        case 'accounts': return `/account/${item.slug}`;
        case 'projects': return item.account ? `/account/${item.account}/project/${item._id}` : '#';
        case 'tasks':    return item.account && item.project
            ? `/account/${item.account}/project/${item.project}`
            : '#';
        case 'groups':   return item.account ? `/account/${item.account}` : '#';
        case 'users':    return `/user/${item._id}`;
        default: return '#';
    }
};

const flattenResults = (results, activeTab) => {
    if (activeTab === 'all') {
        return ALL_TYPES.flatMap(type =>
            (results[type] || []).map(item => ({ type, item }))
        );
    }
    return (results[activeTab] || []).map(item => ({ type: activeTab, item }));
};

// ─── Component ────────────────────────────────────────────────────────────────

const SearchModal = ({ anchorEl, open, onClose, results, loading, activeTab, onTabChange, query, onViewAll }) => {
    const navigate = useNavigate();
    const [highlightIndex, setHighlightIndex] = useState(0);
    const listRef = useRef(null);

    const flatItems = flattenResults(results, activeTab);
    const totalResults = ALL_TYPES.reduce((sum, t) => sum + (results?.totals?.[t] || 0), 0);

    useEffect(() => {
        setHighlightIndex(0);
    }, [results, activeTab]);

    const handleNavigate = useCallback((type, item) => {
        onClose();
        navigate(getItemUrl(type, item));
    }, [navigate, onClose]);

    useEffect(() => {
        if (!open) return;
        const handleKey = (e) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightIndex(i => Math.min(i + 1, flatItems.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightIndex(i => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && flatItems[highlightIndex]) {
                const { type, item } = flatItems[highlightIndex];
                handleNavigate(type, item);
            } else if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [open, flatItems, highlightIndex, handleNavigate, onClose]);

    useEffect(() => {
        const el = listRef.current?.querySelector(`[data-index="${highlightIndex}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlightIndex]);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            PaperProps={{
                sx: {
                    width: anchorEl?.offsetWidth || 500,
                    minWidth: 400,
                    maxHeight: 520,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <StyledTabs
                value={activeTab === 'all' ? 'all' : activeTab}
                onChange={(_, v) => onTabChange(v)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ sx: { height: 2 } }}
            >
                <StyledTab
                    label={
                        <TabLabelBox>
                            All
                            {totalResults > 0 && (
                                <TabCountChip size="small" label={totalResults} />
                            )}
                        </TabLabelBox>
                    }
                    value="all"
                />
                {ALL_TYPES.map(type => {
                    const count = results?.totals?.[type] || 0;
                    return (
                        <StyledTab
                            key={type}
                            label={
                                <TabLabelBox>
                                    {TYPE_CONFIG[type].label}
                                    {count > 0 && (
                                        <TabCountChip size="small" label={count} />
                                    )}
                                </TabLabelBox>
                            }
                            value={type}
                        />
                    );
                })}
            </StyledTabs>

            <ResultsArea ref={listRef}>
                {loading ? (
                    <LoadingBox>
                        <CircularProgress size={24} />
                    </LoadingBox>
                ) : flatItems.length === 0 ? (
                    <EmptyBox>
                        <Typography variant="body2" color="text.secondary">No results found</Typography>
                    </EmptyBox>
                ) : (
                    <List dense disablePadding>
                        {flatItems.map(({ type, item }, idx) => {
                            const { Icon } = TYPE_CONFIG[type];
                            const isHighlighted = idx === highlightIndex;
                            const isDeleted = item.isDeleted;

                            return (
                                <React.Fragment key={`${type}-${item._id}`}>
                                    <ResultListItem
                                        data-index={idx}
                                        onClick={() => handleNavigate(type, item)}
                                        onMouseEnter={() => setHighlightIndex(idx)}
                                        $isDeleted={isDeleted}
                                        $isHighlighted={isHighlighted}
                                    >
                                        <ResultItemIcon>
                                            <Icon fontSize="small" color={isDeleted ? 'disabled' : 'action'} />
                                        </ResultItemIcon>
                                        <ListItemText
                                            primary={
                                                <PrimaryBox>
                                                    <ItemName variant="body2" $isDeleted={isDeleted}>
                                                        {getItemLabel(type, item)}
                                                    </ItemName>
                                                    {isDeleted && (
                                                        <Chip size="small" variant="outlined" color="error" label="Deleted" />
                                                    )}
                                                    {STATUS_DOMAIN[type] && item.status && (
                                                        <StatusBadge domain={STATUS_DOMAIN[type]} status={item.status} disabled={isDeleted} />
                                                    )}
                                                    {(type === 'clients' || type === 'users') && (
                                                        <ActiveChip status={item.active ? 'Active' : 'Inactive'} disabled={isDeleted} />
                                                    )}
                                                </PrimaryBox>
                                            }
                                            secondary={
                                                <SecondaryCaption variant="caption" $isDeleted={isDeleted}>
                                                    {getItemSecondary(type, item)}
                                                </SecondaryCaption>
                                            }
                                            primaryTypographyProps={{ component: 'div' }}
                                        />
                                    </ResultListItem>
                                    {idx < flatItems.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </ResultsArea>

            <FooterBox>
                <Typography variant="caption" color="text.secondary">↑↓ Navigate</Typography>
                <Typography variant="caption" color="text.secondary">↵ Select</Typography>
                <Typography variant="caption" color="text.secondary">ESC Close</Typography>
                <ResultCount variant="caption" color="text.secondary">
                    {flatItems.length} result{flatItems.length !== 1 ? 's' : ''}
                </ResultCount>
                {onViewAll && flatItems.length > 0 && (
                    <ViewAllLink
                        variant="caption"
                        color="primary"
                        onClick={() => { onClose(); onViewAll(query, activeTab); }}
                    >
                        View all →
                    </ViewAllLink>
                )}
            </FooterBox>
        </Popover>
    );
};

export default SearchModal;
